import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useReducedMotion } from 'framer-motion';
import { Button, ButtonProps } from '@mui/material';

interface MagneticButtonProps extends ButtonProps {
  strength?: number; // how far it leans toward the cursor (px)
}

/**
 * A button that subtly leans toward the cursor while hovered, then springs
 * back on leave. The transform lives on a wrapping motion.div so MUI's Button
 * props stay intact. Disabled under reduced-motion.
 */
const MagneticButton: React.FC<MagneticButtonProps> = ({
  strength = 14,
  children,
  ...props
}) => {
  const prefersReduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 250, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 250, damping: 18, mass: 0.4 });

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (prefersReduced || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const relX = e.clientX - (rect.left + rect.width / 2);
    const relY = e.clientY - (rect.top + rect.height / 2);
    x.set((relX / (rect.width / 2)) * strength);
    y.set((relY / (rect.height / 2)) * strength);
  };

  const reset = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={{
        display: 'inline-block',
        ...(prefersReduced ? {} : { x: sx, y: sy }),
      }}
    >
      <Button {...props}>{children}</Button>
    </motion.div>
  );
};

export default MagneticButton;
